export const Grooming_SOP = {
    preGroomInspection: {
        title: "Pre-Groom Health & Coat Check",
        image: "https://pawsible-s3.s3.us-west-1.amazonaws.com/training_images/guide_images/preGroomInspectionTitle.png",
        body: `
          <section>
            <h3>🔍 Step One of Every Groom: Observe First</h3>
            <p>
              A thorough pre-groom check ensures we don't just style pets — we protect their wellbeing. 
              <span class="highlight">Spotting health concerns early earns client trust and prevents grooming complications.</span>
              Always perform this check <strong>before the pet enters the bath or grooming table</strong>.
            </p>
          </section>
      
          <section>
            <h4>🐶 External Health Scan</h4>
            <ul>
              <li>👁️ Check eyes: any discharge, cloudiness, or irritation?</li>
              <li>👂 Ears: red, dirty, or foul-smelling? </li>
              <li>🦷 Teeth and gums: note visible tartar or unusual breath</li>
              <li>🩹 Look for bumps, sores, scabs, or growths on body</li>
              <li>🐾 Check paw pads for cracks, swelling, or foreign debris</li>
              <li>🪳 Scan for fleas, ticks, or skin inflammation</li>
            </ul>
          </section>
      
          <section>
            <h4>🧴 Coat & Matting Assessment</h4>
            <ul>
              <li>✂️ Check coat type and texture: curly, double, wiry, etc.</li>
              <li>🧶 Identify mats, tangles, or packed undercoat (esp. in armpits, behind ears, tail base)</li>
              <li>💦 Determine if a de-shed or mat removal is needed before bath</li>
              <li>📸 Log severe matting for client transparency and upcharge if applicable</li>
            </ul>
            <p><span class="highlight-blue">Never attempt to brush out severe mats — communicate and shave if required to avoid skin problem and stressing pets.</span></p>
            <div class="embedded-visual">
              <img loading="lazy" src="https://pawsible-s3.s3.us-west-1.amazonaws.com/training_images/guide_images/preGroomInspectionImage.png" alt="Real-Life Incident Example" class="inline-guide-image" />
            </div>
          </section>
      
          <section>
            <h4>📋 Document & Communicate</h4>
            <ul>
              <li>🗒️ Log all findings in the grooming dashboard under specific pet</li>
              <li>📞 Alert front desk or parent if anything looks urgent or warrants a vet check</li>
              <li>💬 Confirm service expectations if coat condition differs from what was booked</li>
            </ul>
            <p><span class="highlight-green">Clarity builds trust. Clients appreciate honest updates and proactive care.</span></p>
          </section>
      
          <section>
            <h4>🌱 Final Note</h4>
            <p>
              <span class="highlight">Your eyes and hands are the first layer of wellness care.</span> A solid pre-check helps prevent injury, ensure comfort, and elevate the grooming experience — for both pet and parent.
            </p>
          </section>
        `
      },
      groomingPhotoSnapshots: {
        title: "Photo/Video Documentation for Grooming Guests",
        image: "https://pawsible-s3.s3.us-west-1.amazonaws.com/training_images/guide_images/groomingPhotoSnapshotsTitle.png",
        body: `
          <section>
            <h3>📸 Protect, Communicate, Celebrate: Photo/Video Protocols for Grooming</h3>
            <p>
              At Pawsible, <span class="highlight">photo and video documentation</span> is a core part of <span class="highlight-green">professional grooming, clear client communication, and risk management</span>. 
              Proper visual records protect staff, set client expectations, and showcase the quality of care we deliver.
            </p>
          </section>
      
          <section>
            <h4>🎯 1. Purpose of Grooming Photos/Videos</h4>
            <ul>
              <li>🛡️ Document pre-existing matting, skin conditions, or injuries clearly before service</li>
              <li>📈 Showcase dramatic transformations for client satisfaction (before/after comparison)</li>
              <li>🚨 Support client communication when style adjustments, coat damage, or concerns arise</li>
              <li>🐾 Celebrate beautiful grooms internally and externally (with client consent)</li>
            </ul>
            <p><span class="highlight-blue">A well-timed photo protects your work — and proves your care and transparency.</span></p>
          </section>
      
          <section>
            <h4>📋 2. When to Capture Photos/Videos</h4>
            <ul>
              <li>👀 Before starting if:
                <ul>
                  <li>Matting is severe or scattered</li>
                  <li>Skin irritation, rashes, wounds, or growths are visible</li>
                  <li>Coat condition is poor or may require a "shave down" discussion</li>
                </ul>
              </li>
              <li>📸 After completion:
                <ul>
                  <li>Final groom results: coat finish, style execution</li>
                  <li>Special grooming requests fulfilled ("teddy bear cut," "lion trim," etc.)</li>
                </ul>
              </li>
              <li>🚨 If any incident occurs (clipper irritation, unexpected reactions, nicked skin)</li>
            </ul>
            <p><span class="highlight-green">Photos help set clear expectations before starting — and celebrate the results after finishing.</span></p>
          </section>
      
          <section>
            <h4>📸 3. Best Practices for Grooming Documentation</h4>
            <ul>
              <li>🔆 Use natural or bright lighting — ensure coat color, texture, and skin are clearly visible</li>
              <li>📏 Frame full-body shots plus close-ups of any problem areas if needed</li>
              <li>🧼 Keep grooming stations tidy in the background — messy environments hurt perception</li>
              <li>🎯 <span class="highlight-red">Limit to 2–3 strong images</span> rather than overwhelming with multiples</li>
              <li>📲 Upload photos promptly on team chat under Grooming</li>
            </ul>
          </section>
      
          <section>
            <h4>📲 4. Uploading and Sharing Guidelines</h4>
            <ul>
              <li>🖼️ Upload to the team chat under Grooming</li>
              <li>💬 Include short captions on content highlights ("Max's before/after haircut!")</li>
              <li>🛡️ Only staff-approved images may be used for public social media posts</li>
            </ul>
            <p><span class="highlight-blue">Photo documentation is a professional service — treat it with the same pride as hands-on care.</span></p>
          </section>
      
          <section>
            <h4>🌱 Final Thought</h4>
            <p>
              <span class="highlight">Professional grooming is about skill, care — and clear communication.</span> 
              Visual documentation at Pawsible elevates our service, protects our work, and builds lasting trust with every family we serve.
            </p>
          </section>
        `
      },      
      groomingClientAlerts: {
        title: "Client Alerts & Special Notes",
        image: "https://pawsible-s3.s3.us-west-1.amazonaws.com/training_images/guide_images/groomingClientAlertsTitle.png",
        body: `
          <section>
            <h3>📣 Grooming Alerts = Grooming Safety</h3>
            <p>
              Clear notes prevent mistakes, reactivity, or missed needs. <span class="highlight">Every grooming appointment should begin with a review of alerts tied to the pet’s file.</span> These notes protect everyone — the pet, the groomer, and the brand.
            </p>
          </section>
      
          <section>
            <h4>🔴 Types of Alerts to Check Before Grooming</h4>
            <ul>
              <li>💊 <strong>Medical Needs:</strong> Allergies, recent surgery, skin sensitivity, seizure history</li>
              <li>⚠️ <strong>Behavioral Notes:</strong> Doesn’t like paws touched, bites during nail trims, anxious in bath</li>
              <li>📏 <strong>Style Preferences:</strong> Past cuts the client disliked, favorite trim style, length reminders</li>
              <li>⏰ <strong>Time Flags:</strong> Parents must pick up by a certain time or pet tires easily</li>
            </ul>
          </section>
      
          <section>
            <h4>🗂️ Where to Find These Notes</h4>
            <ul>
              <li>📲 <strong>Grooming tasks dashboard</strong> – pre-checks and client notes should be logged here</li>
              <li>🧠 Ask teammates or front desk if any note seems unclear or missing</li>
            </ul>
            <p><span class="highlight-red">When in doubt, confirm before you clip. Communication gaps create client dissatisfaction — or worse, risk.</span></p>
          </section>
      
          <section>
            <h4>💬 Client Communication & App Integration</h4>
            <ul>
              <li>📱 <span class="highlight">Our app auto-texts parents</span> when pets are taken into bath, grooming, or marked complete</li>
              <li>📞 <strong>Do not call clients directly</strong> — ask front desk to contact the parent if clarification is needed</li>
              <li>📝 Ensure notes are updated immediately if a style is changed or a concern is spotted</li>
            </ul>
            <div class="embedded-visual">
              <img loading="lazy" src="https://pawsible-s3.s3.us-west-1.amazonaws.com/training_images/guide_images/groomingClientAlertsImage.png" alt="Real-Life Incident Example" class="inline-guide-image" />
            </div>
            <p><span class="highlight-blue">Consistency in how we communicate keeps the experience professional — and parents assured.</span></p>
          </section>
      
          <section>
            <h4>✏️ Updating Notes After Grooming</h4>
            <ul>
              <li>✅ Add any new observations (e.g., “Matting near ears, switched to shorter cut with parent approval”)</li>
              <li>📸 <span class="highlight-red">Attach before/after photos</span> for transparency in team chat - great media content</li>
              <li>🧼 Flag any future considerations (“Calms down after shower")</li>
            </ul>
            <p><span class="highlight-green">We don’t just groom pets — we track their wellness, preferences, and progress.</span></p>
          </section>
      
          <section>
            <h4>🌱 Final Thought</h4>
            <p>
              <span class="highlight">Every note is a gift to the next grooming.</span> Take the time to read and write with care. It saves time, deepens trust, and keeps pets safe — one file at a time.
            </p>
          </section>
        `
      },
      bathingProtocols: {
        title: "Bathing Protocols & Coat Prep",
        image: "https://pawsible-s3.s3.us-west-1.amazonaws.com/training_images/guide_images/bathingProtocolsTitle.png",
        body: `
          <section>
            <h3>🛁 Bathing Isn’t Just Cleaning — It’s Preparation</h3>
            <p>
              A proper bath sets the tone for a successful groom. <span class="highlight">Coat texture, skin sensitivity, and drying method all impact the final result</span>. At Pawsible, our bath SOPs ensure safety, comfort, and style readiness for every pup.
            </p>
          </section>
      
          <section>
            <h4>🔍 Before You Begin</h4>
            <ul>
              <li>📋 Review pet file for medical or behavioral alerts (e.g., seizures, ear infections, fear of sprayer)</li>
              <li>🧼 Brush out mats or tangles gently with detangling spray — <span class="highlight-red">never bathe a matted dog without prep</span></li>
              <li>🧴 Choose shampoo based on skin type (sensitive, deodorizing, flea/tick, hypoallergenic)</li>
              <li>🧠 Use cotton balls in ears if pup tolerates — this prevents water from entering canals</li>
            </ul>
            <p><span class="highlight-yellow">Bathing a matted dog without prep can worsen tangles and cause skin irritation. Always check before you soak.</span></p>
          </section>
      
          <section>
            <h4>🛁 Bathing Process</h4>
            <ul>
              <li>💧 Use warm (not hot) water and wet the coat fully — avoid blasting water near face or ears</li>
              <li>🧴 Lather shampoo from neck down, massaging into skin — pay attention to groin, paws, and undercoat</li>
              <li>🔁 Rinse twice — shampoo residue causes itchiness and flaking</li>
              <li>🧽 Apply conditioner if needed — especially for long coats or dry skin</li>
              <li>🪮 Comb through coat gently in tub if coat allows — especially on doodles, long-haired breeds</li>
            </ul>
            <p><span class="highlight-blue">Rinse and pressure should always be calming. The goal is a relaxed, clean pup — not just a rushed wash.</span></p>
          </section>
      
          <section>
            <h4>💨 Drying & Coat Prep</h4>
            <ul>
              <li>🧴 Towel blot first — avoid rough rubbing that causes friction or frizz</li>
              <li>💨 Use velocity dryer on adequate setting — always watch for stress signals</li>
              <li>🧠 Cover ears for sound-sensitive pups with cotton balls</li>
              <li>🪮 Brush or fluff dry in sections — <span class="highlight-red">coat must be fully dry and detangled before grooming</span></li>
            </ul>
            <p><span class="highlight-green">A clean, dry, and prepped coat = a smooth, safe, and efficient groom.</span></p>
            <div class="embedded-visual">
                <img loading="lazy" src="https://pawsible-s3.s3.us-west-1.amazonaws.com/training_images/guide_images/bathingProtocolsImage.png" alt="Real-Life Incident Example" class="inline-guide-image" />
            </div>
          </section>
      
          <section>
            <h4>📸 Final Check Before Styling</h4>
            <ul>
              <li>✅ Skin fully visible? No damp spots?</li>
              <li>✅ No hidden mats or tight tangles?</li>
              <li>✅ Behavior okay for moving to grooming table?</li>
              <li>✅ Update notes if any rash, hotspot, or change was observed during bath</li>
            </ul>
          </section>
      
          <section>
            <h4>🌱 Final Thought</h4>
            <p>
              <span class="highlight">Baths aren’t just prep — they’re readiness for beautiful haircut.</span> A calm, clean start means less stress, better styles, and happier parents.
            </p>
          </section>
        `
      },
      mattingPolicyProcedure: {
        title: "Matting Policy & Procedure",
        image: "https://pawsible-s3.s3.us-west-1.amazonaws.com/training_images/guide_images/mattingPolicyProcedureTitle.png",
        body: `
          <section>
            <h3>🧩 Safety First, Style Second</h3>
            <p>Matting isn’t just a cosmetic issue — it can hide wounds, cause pain, and affect a dog’s physical and emotional wellbeing. <span class="highlight">We prioritize comfort, health, and transparent communication over preserving length.</span></p>
          </section>
      
          <section>
            <h4>🔍 How & When to Brush Out</h4>
            <ul>
              <li>🖐️ Do not brush out mats while dry unless very mild — always use detangler spray or conditioner</li>
              <li>🧼 Light matting can be brushed out <em>before the bath</em> to prevent tightening with water</li>
              <li>🧴 For heavier matting, soak in conditioner, let it sit, then assess before attempting removal</li>
              <li>✂️ If brushing causes discomfort or the coat is pelted, <span class="highlight-red">clip down is required after communicating with the owner</span></li>
            </ul>
            <div class="embedded-visual">
                <img loading="lazy" src="https://pawsible-s3.s3.us-west-1.amazonaws.com/training_images/guide_images/mattingPolicyProcedureImage.png" alt="Real-Life Incident Example" class="inline-guide-image" />
            </div>
          </section>
      
          <section>
            <h4>🗣️ Client Communication</h4>
            <ul>
              <li>📷 Always take pre-groom photos to document condition</li>
              <li>💬 Never promise a desired haircut without inspecting the coat — be transparent</li>
              <li>📞 For clip-downs or unexpected changes, notify the front desk to contact the client</li>
            </ul>
            <p><span class="highlight-blue">Matting doesn’t mean failure — it’s a chance to educate and rebuild coat health.</span></p>
          </section>
      
          <section>
            <h4>📋 Final Note</h4>
            <p><span class="highlight-green">We don’t shame — we support.</span> Treat each case with care, explain with empathy, and prioritize pet comfort above all.</p>
          </section>
        `
      },
      nailTrimming: {
        title: "Nail Trimming & Paw Care",
        image: "https://pawsible-s3.s3.us-west-1.amazonaws.com/training_images/guide_images/nailTrimmingTitle.png",
        body: `
          <section>
            <h3>🐾 Small Details, Big Impact</h3>
            <p>Proper nail care is essential for comfort, posture, and overall health. At Pawsible, <span class="highlight">we treat nail trimming as part of full-body wellness, not just a grooming add-on.</span></p>
          </section>
      
          <section>
            <h4>🧰 Tools & Prep</h4>
            <ul>
              <li>✂️ Use sharp, clean nail clippers sized for the pet (guillotine or scissor style)</li>
              <li>🖌️ <span class="highlight-green">Keep styptic powder nearby</span> for accidents</li>
              <li>🧼 Wipe paws before trimming to remove debris and moisture</li>
            </ul>
          </section>
      
          <section>
            <h4>✂️ Nail Trimming Technique</h4>
            <ul>
              <li>👀 Identify the quick — trim a small amount at a time if unsure or nail color too dark</li>
              <li>🕯️ Use a flashlight under darker nails if needed</li>
              <li>✋ Hold the paw gently but firmly to minimize movement</li>
              <li>🔁 Recheck all paws after completion to confirm evenness</li>
            </ul>
            <p><span class="highlight-blue">Always go slow — hurting a pet can create lasting fear.</span></p>
          </section>
      
          <section>
            <h4>🧠 Behavior Handling</h4>
            <ul>
            <li>🧘 <span class="highlight-red">Always use muzzle if needed, safety FIRST</span></li>
            <li>🧘 Use calming techniques for anxious pets: soft voice, gentle hold, breaks if needed</li>
            <li>🚩 If a pet is uncontrollable, notify lead groomer or pause and reassess</li>
              <li>📝 Document any resistance or behavior notes in the grooming dashboard</li>
            </ul>
              <div class="embedded-visual">
                <img loading="lazy" src="https://pawsible-s3.s3.us-west-1.amazonaws.com/training_images/guide_images/nailTrimmingImage.png" alt="Real-Life Incident Example" class="inline-guide-image" />
            </div>
          </section>
      
          <section>
            <h4>🦶 Extra Paw Care</h4>
            <ul>
              <li>🧼 Clean between toes and check for debris, foxtails, or signs of irritation</li>
              <li>🧴 Offer paw balm for clients who opt-in to extra TLC</li>
              <li>📸 For cracked pads or wounds, update memo to alert owner</li>
            </ul>
          </section>
      
          <section>
            <h4>📋 Final Checks</h4>
            <ul>
              <li>✅ Confirm nails are trimmed evenly and not clicking on the floor</li>
              <li>✅ Apply quick stop if bleeding occurs and notify lead or manager immediately</li>
              <li>✅ Clean and sanitize tools between pets</li>
            </ul>
          </section>
      
          <section>
            <h4>🌱 Final Thought</h4>
            <p><span class="highlight-green">Calm hands create confident paws.</span> Gentle, thorough paw care shows our attention to the details clients can’t always see — but pets always feel.</p>
          </section>
        `
      },
      groomingSteps: {
        title: "Haircut Flow & Styling Steps",
        image: "https://pawsible-s3.s3.us-west-1.amazonaws.com/training_images/guide_images/groomingStepsTitle.png",
        body: `
          <section>
            <h3>✂️ Consistency, Safety & Style</h3>
            <p>
              Every haircut at Pawsible is more than aesthetic — it's a <span class="highlight">reflection of our care, precision, and breed knowledge</span>. Whether it’s a simple trim or a full-body style, groomers must prioritize safety, technique, and clarity with client expectations.
            </p>
          </section>
      
          <section>
            <h4>🪞 1. Pre-Cut Brief & Reference</h4>
            <ul>
              <li>📸 <strong>Review client photos or style requests</strong> from the appointment notes</li>
              <li>🧠 <strong>Assess coat condition & limitations</strong> — don’t attempt styles that compromise comfort or safety</li>
              <li>💬 Clarify any unclear notes with front desk before starting</li>
            </ul>
            <p><span class="highlight-blue">Grooming is collaborative — align expectations before you begin.</span></p>
          </section>
      
          <section>
            <h4>🔧 2. Tools & Station Prep</h4>
            <ul>
              <li>🧼 Sanitize and lay out all clipper blades, combs, scissors, and de-matting tools</li>
              <li>⚙️ Set up proper lighting and elevation for ergonomic support</li>
              <li>🔌 Double-check blade lengths and clipper guards</li>
            </ul>
          </section>
      
          <section>
            <h4>✂️ 3. Head-to-Tail Grooming Sequence</h4>
            <ul>
              <li>👁️ <strong>Eyes & face:</strong> Use rounded-tip shears for face shaping; avoid obstructing vision</li>
              <li>👂 <strong>Ears:</strong> Clean before trimming; gently scissor to client’s style request</li>
              <li>🫱 <strong>Legs & feet:</strong> Scissor around pads and trim feathering — ensure even weight on all limbs</li>
              <li>🔄 <strong>Body:</strong> Follow natural coat direction; use long strokes for even blending</li>
              <li>🦶 <strong>Sanitary trim:</strong> Carefully shave belly/groin area and under tail with guard or #10 blade</li>
              <li>🪮 <strong>Final brush-through:</strong> Double-check for uneven areas or flyaways</li>
            </ul>
            <div class="embedded-visual">
              <img loading="lazy" src="https://pawsible-s3.s3.us-west-1.amazonaws.com/training_images/guide_images/groomingStepsImage.png" alt="Real-Life Incident Example" class="inline-guide-image" />
            </div>
            <p><span class="highlight-blue">Always prioritize pet comfort — if a dog shows signs of stress, pause and soothe before continuing.</span></p>
          </section>
      
          <section>
            <h4>📏 4. Blending, Finishing & Signature Touch</h4>
            <ul>
              <li>🔁 Check all transitions (neck, shoulder, hips) for seamless blending</li>
              <li>💨 Blow off loose hair; apply coat conditioner or finishing spray if requested</li>
              <li>🎀 Finish with pet cologne or leave off per client notes</li>
            </ul>
          </section>
      
          <section>
            <h4>🧾 5. Post-Groom Handoff Notes</h4>
            <ul>
              <li>🗒️ Note haircut length used, tools, behavior (movement, resistance, tolerance)</li>
              <li>📲 Log any matting, sensitive skin, or areas that needed extra care in grooming dashboard</li>
              <li>🔁 Recommend maintenance schedule for coat type or behavior</li>
            </ul>
            <p><span class="highlight-green">Clear notes create repeatable results — and happy returning clients.</span></p>
          </section>
      
          <section>
            <h4>🌱 Final Thought</h4>
            <p>
              <span class="highlight">Great grooming is repeatable, personalized, and built on trust.</span> Show your skill with precision, care, and confidence — every haircut is a chance to build a lifelong client.
            </p>
          </section>
        `
      },
      handlingDifficultPets: {
        title: "Handling Anxious or Reactive Pets",
        image: "https://pawsible-s3.s3.us-west-1.amazonaws.com/training_images/guide_images/handlingDifficultPetsTitle.png",
        body: `
          <section>
            <h3>🧠 Empathy-First Handling</h3>
            <p>
              Not every pet walks in ready to be pampered. Some carry past trauma, others are simply overstimulated. 
              At Pawsible, <span class="highlight">we lead with patience, safety, and emotional awareness</span> — treat them like your dog.
            </p>
          </section>
      
          <section>
            <h4>🔎 1. Identifying Stress Signals</h4>
            <ul>
              <li>🐾 Trembling, hiding, or tail tucking</li>
              <li>👀 Whale eye, lip licking, or yawning</li>
              <li>🐶 Growling, lunging, or vocalizing near triggers</li>
              <li>🌀 Freezing or frantic pacing</li>
            </ul>
            <p><span class="highlight-red">If signs appear, stop and reassess. Never proceed forcefully.</span></p>
          </section>
      
          <section>
            <h4>🧘 2. Calming Techniques</h4>
            <ul>
              <li>🧸 Allow sniffing and space to settle before contact</li>
              <li>🗣️ Speak softly, move slowly — keep energy grounded</li>
              <li>🪑 Minimize restraint; use gentle handling and allow breaks</li>
              <li>🧴 Apply calming sprays or tools if previously approved</li>
              <li>🌬️ Maintain airflow and a quiet, low-stress setting</li>
            </ul>
            <p><span class="highlight-blue">Comfort creates cooperation. Let trust guide the tempo.</span></p>
            <div class="embedded-visual">
              <img loading="lazy" src="https://pawsible-s3.s3.us-west-1.amazonaws.com/training_images/guide_images/handlingDifficultPetsImage.png" alt="Real-Life Incident Example" class="inline-guide-image" />
            </div>
          </section>
      
          <section>
            <h4>🛡️ 3. When Reactivity Escalates</h4>
            <ul>
              <li>👥 Always have backup when handling known reactive pets</li>
              <li>🧤 Use muzzles, slip leads, or cones only if necessary</li>
              <li>📲 Flag reactivity in the pet profile with detailed notes</li>
              <li>🧽 Sanitize tools and surfaces after session to reduce scent triggers</li>
            </ul>
            <p><span class="highlight-red">If safety feels uncertain, stop immediately and notify a supervisor.</span></p>
          </section>
      
          <section>
            <h4>📣 4. Owner Communication</h4>
            <ul>
              <li>💬 “We took extra care today as Luna was a little nervous.”</li>
              <li>🧾 “We’ve noted her behavior so we can prep a better plan next time.”</li>
              <li>📆 “Would you like to schedule a short decompression session before future grooms?”</li>
            </ul>
            <p><span class="highlight-green">Speak with empathy, not alarm. The goal is collaborative care.</span></p>
          </section>
      
          <section>
            <h4>🗂️ 5. Documentation & Dashboard Alerts</h4>
            <ul>
              <li>📝 Use Incident Log if aggression or escalation occurred</li>
              <li>📌 Add behavioral tags in the pet's profile</li>
              <li>📲 Update team on Tasks Dashboard so next staff are prepared</li>
            </ul>
            <p><span class="highlight-blue">A well-documented pet history protects both pets and staff.</span></p>
          </section>
      
          <section>
            <h4>🌟 Final Thought</h4>
            <p>
              <span class="highlight">The goal isn’t perfect behavior — it’s creating a safe and supportive experience.</span> 
              Kind handling today builds trust for tomorrow.
            </p>
          </section>
        `
      },
      groomingProductUsage: {
        title: "Product Usage & Allergy Awareness",
        image: "https://pawsible-s3.s3.us-west-1.amazonaws.com/training_images/guide_images/groomingProductUsageTitle.png",
        body: `
          <section>
            <h3>🧴 Know Your Products, Know Your Pets</h3>
            <p>
              Every product we use at Pawsible — from shampoos to sprays — has an impact on the pet’s comfort, coat health, and safety. 
              <span class="highlight">Our role is to be intentional and informed with every application.</span>
            </p>
          </section>
      
          <section>
            <h4>🧪 1. Product Familiarity & Ingredient Check</h4>
            <ul>
              <li>📚 Know what’s in the products — familiarize yourself with ingredients and common triggers (e.g., oatmeal, lavender, coconut, tea tree)</li>
              <li>🧼 Only use Pawsible-approved grooming products stocked in the salon unless provided by clients</li>
              <li>🔍 Read labels before using anything new or client-provided</li>
              <li>🧪 Avoid mixing different product brands unless pre-approved by management</li>
            </ul>
            <p><span class="highlight-blue">When in doubt, ask — never assume an unfamiliar product is safe for all pets.</span></p>
          </section>
      
          <section>
            <h4>📝 2. Allergy Notes & Paw Parent Preferences</h4>
            <ul>
              <li>📋 Check client notes for known allergies, skin sensitivities, or scent aversions</li>
              <li>💬 Confirm product requests or restrictions during intake or front desk check-in</li>
              <li>🧴 For unscented or hypoallergenic requests, double-check bottles before use</li>
            </ul>
            <p><span class="highlight-green">Respecting skin sensitivities is not optional — it's part of professional care.</span></p>
            <div class="embedded-visual">
              <img loading="lazy" src="https://pawsible-s3.s3.us-west-1.amazonaws.com/training_images/guide_images/groomingProductUsageImage.png" alt="Real-Life Incident Example" class="inline-guide-image" />
            </div>
          </section>
      
          <section>
            <h4>🔄 3. Substituting Products When Needed</h4>
            <ul>
              <li>🔄 If a requested product is out of stock, notify the front desk before substituting</li>
              <li>🧠 Use the most similar hypoallergenic or vet-approved product available</li>
              <li>📲 Log any substitutions or changes in the pet’s file for transparency</li>
            </ul>
            <p><span class="highlight-blue">Substitution should never surprise the pet parent — communication matters.</span></p>
          </section>
      
          <section>
            <h4>🚨 4. Reactions & Reporting Protocol</h4>
            <ul>
              <li>👁️ Watch for signs of product reactions: excessive itching, redness, rash, hives, or hot spots</li>
              <li>🧯 Rinse immediately with cool water if reaction is suspected and inform a supervisor</li>
              <li>📞 Notify the pet parent with a calm, factual report</li>
              <li>📝 File a grooming incident report and tag the product used</li>
            </ul>
            <p><span class="highlight-red">Safety first — fast reporting and transparency protect everyone involved.</span></p>
          </section>
      
          <section>
            <h4>🌿 5. Eco & Ethical Awareness</h4>
            <ul>
              <li>🌎 Prioritize eco-friendly, cruelty-free products aligned with Pawsible values</li>
              <li>📦 Properly store and dispose of products to avoid contamination or leaks</li>
              <li>♻️ Report any damaged or expired items immediately for replacement</li>
            </ul>
          </section>
      
          <section>
            <h4>🌱 Final Thought</h4>
            <p>
              <span class="highlight">Grooming is skin-deep — literally.</span> The products we choose can soothe, protect, or irritate. 
              Let’s stay aware, stay respectful, and always choose what’s best for the pet.
            </p>
          </section>
        `
      },
      groomingTimeStandards: {
        title: "Time Standards & Workflow Tips",
        image: "https://pawsible-s3.s3.us-west-1.amazonaws.com/training_images/guide_images/groomingTimeStandardsTitle.png",
        body: `
          <section>
            <h3>⏱️ Time Matters — But So Does Quality</h3>
            <p>
              At Pawsible, grooming is paced for <span class="highlight">efficiency without rushing</span>. We value punctuality, but never at the expense of pet comfort or craftsmanship.
            </p>
          </section>
      
          <section>
            <h4>🕐 1. Ideal Time Standards</h4>
            <ul>
              <li>🚿 <strong>Bath-Only:</strong> 30–40 minutes (includes prep, dry, and brush-out)</li>
              <li>✂️ <strong>Full Groom:</strong> 60–90 minutes for most small to medium breeds</li>
              <li>🐩 <strong>Haircuts with de-matting or anxious pets:</strong> up to 2 hours as needed</li>
              <li>🛁 <strong>Double-coated breeds:</strong> allow 1–2 hours for proper drying of undercoat (ex. Siberian Husky)</li>
            </ul>
            <p><span class="highlight-blue">If you expect to exceed these windows, inform the front desk to update the pet parent.</span></p>
          </section>
      
          <section>
            <h4>⚙️ 2. Efficient Grooming Flow</h4>
            <ul>
              <li>🧑‍🤝‍🧑 <strong>Teamwork flow:</strong> Bather completes assessment, bath, de-shed, and nail trim — groomer takes over for drying, styling, and finishing touches</li>
              <li>🐕 <strong>Short-style dogs:</strong> Begin with the haircut to reduce drying time post-bath</li>
              <li>🧼 <strong>Long-style dogs:</strong> Bathe first to pamper and prep the coat for detailed scissoring</li>
              <li>🧹 <strong>Double-coated breeds (Deshedding):</strong> Perform de-shedding before the bath to loosen the undercoat</li>
              <li>✂️ Work head-to-tail with a consistent system — avoid skipping around</li>
              <li>🪮 Final brush-through last to catch flyaways and polish the finish</li>
            </ul>
            <p><span class="highlight-green">A predictable, team-driven flow reduces fatigue and keeps pets calmer throughout the experience.</span></p>
          </section>
      
          <section>
            <h4>📦 3. Tips for Staying on Track</h4>
            <ul>
              <li>⏲️ Set a timer for each phase if you tend to lose track (e.g., 15 min for dry, 30 min for cut)</li>
              <li>🔋 Use time-saving tools (high-velocity dryers, pre-mixed spritzers, guard combs)</li>
              <li>🔄 Tag in a teammate for drying or bath help during busy hours</li>
              <li>👥 Ask for support instead of pushing through with a reactive or difficult pet</li>
            </ul>
            <p><span class="highlight-blue">Efficiency doesn’t mean rushing — it means being proactive and prepared.</span></p>
            <div class="embedded-visual">
              <img loading="lazy" src="https://pawsible-s3.s3.us-west-1.amazonaws.com/training_images/guide_images/groomingTimeStandardsImage.png" alt="Real-Life Incident Example" class="inline-guide-image" />
            </div>
          </section>
      
          <section>
            <h4>📣 4. Communicating Delays</h4>
            <ul>
              <li>📲 Always notify the front desk if you're behind by 20+ minutes</li>
              <li>📞 If a pet is anxious or needs breaks, explain this calmly to the client</li>
              <li>🗂️ Log behavioral reasons in the grooming notes to plan future sessions better</li>
            </ul>
            <p><span class="highlight-red">Delays are okay — silence isn’t. Keep communication open and timely.</span></p>
          </section>
      
          <section>
            <h4>🌱 Final Thought</h4>
            <p>
              <span class="highlight">Time management is a craft — just like grooming.</span> Consistency and flow help you work smarter, not harder, while giving every pet the care they deserve.
            </p>
          </section>
        `
      },
      toolCareStationCleanliness: {
        title: "Tool Care & Station Cleanliness",
        image: "https://pawsible-s3.s3.us-west-1.amazonaws.com/training_images/guide_images/toolCareStationCleanlinessTitle.png",
        body: `
          <section>
            <h3>🧰 Clean Tools, Happy Grooms</h3>
            <p>
              Grooming excellence starts with <span class="highlight">sharp tools and a spotless station</span>. At Pawsible, we care for our equipment and environment like we care for our pets — consistently and thoroughly.
            </p>
          </section>
      
          <section>
            <h4>🪒 1. Daily Tool Care Checklist</h4>
            <ul>
              <li>🧼 Wash combs, brushes, and scissors with pet-safe disinfectant between each pet</li>
              <li>🛢️ Oil clipper blades before, during, and after use to prevent dullness and overheating</li>
              <li>⚙️ Check blade teeth for damage daily — swap out if nicked or uneven</li>
              <li>🧹 Remove hair from clipper vents and dryer filters every shift</li>
            </ul>
            <p><span class="highlight-blue">Proper maintenance extends the life of your tools — and keeps pets safer.</span></p>
          </section>
      
          <section>
            <h4>🧽 2. Station Cleanliness Standards</h4>
            <ul>
              <li>🧹 Sweep hair & vacuum after every pet — not just end of shift</li>
              <li>🪑 Wipe down tables, kennels, and grooming arms with disinfectant after each dog</li>
              <li>🗂️ Keep products neatly organized and labeled at your station</li>
              <li>🗑️ Empty trash bins when full — don't leave debris for the next shift</li>
            </ul>
            <p><span class="highlight-green">Your workspace reflects your professionalism. Clean station = clean reputation.</span></p>
          </section>
      
          <section>
            <h4>🧴 3. Weekly Deep Clean Duties</h4>
            <ul>
              <li>💦 Deep clean grooming tables and walls with designated cleaning solution</li>
              <li>🧺 Launder towels, aprons, and mats — dirty linen should never pile up</li>
              <li>🧯 Sanitize high-touch areas: door handles, dryer switches, shampoo pumps</li>
              <li>🔎 Audit blades, shears, and clippers for any maintenance or replacement needs</li>
            </ul>
            <p><span class="highlight-blue">A polished salon space shows respect — for pets, paw parents, and teammates alike.</span></p>
            <div class="embedded-visual">
              <img loading="lazy" src="https://pawsible-s3.s3.us-west-1.amazonaws.com/training_images/guide_images/toolCareStationCleanlinessImage.png" alt="Real-Life Incident Example" class="inline-guide-image" />
            </div>
          </section>
      
          <section>
            <h4>⚠️ 4. Tool Damage or Malfunction Reporting</h4>
            <ul>
              <li>🚨 Report damaged or malfunctioning tools immediately to management</li>
              <li>🔧 Place broken items in the designated "repair bin" — don’t leave them in service areas</li>
              <li>📋 Log tool issues in the grooming dashboard for visibility</li>
            </ul>
            <p><span class="highlight-red">Using broken or dull equipment risks injury — speak up, don’t patch up.</span></p>
          </section>
      
          <section>
            <h4>🌱 Final Thought</h4>
            <p>
              <span class="highlight">Great grooms aren’t just about skill — they’re about environment.</span> 
              A clean, well-maintained station builds trust, safety, and a team culture of excellence.
            </p>
          </section>
        `
      },
      groomerSafety: {
        title: "Groomer Safety & Ergonomic Practice",
        image: "https://pawsible-s3.s3.us-west-1.amazonaws.com/training_images/guide_images/groomerSafetyTitle.png",
        body: `
          <section>
            <h3>🦺 Safety First, Longevity Always</h3>
            <p>
              Grooming isn’t just hard work — it’s physical work. At Pawsible, we prioritize <span class="highlight">safety, ergonomics, and injury prevention</span> so you can groom happily and healthily for years to come.
            </p>
          </section>
      
          <section>
            <h4>🛡️ 1. Groomer Personal Safety</h4>
            <ul>
              <li>🧤 Wear protective gear as needed: anti-slip shoes, groomer smocks, gloves for bathing or handling difficult pets</li>
              <li>🧯 Know emergency exits and first aid kit locations at all times</li>
              <li>🛑 If a pet shows serious aggression or distress, <strong>stop immediately and escalate</strong> — safety over completion</li>
              <li>🪪 Maintain current pet first aid and CPR certifications if available</li>
            </ul>
            <p><span class="highlight-blue">No groom is worth a serious injury — protect yourself first.</span></p>
          </section>
      
          <section>
            <h4>🪑 2. Ergonomic Grooming Habits</h4>
            <ul>
              <li>🐕 <span class="highlight-red">Never leave pets unattended</span> on tub or table especially with grooming leashes on</li>
              <li>🔧 Adjust grooming tables to hip height to avoid slouching or overreaching</li>
              <li>🔄 Shift positions frequently — don’t lock your knees or hunch your shoulders</li>
              <li>🪜 Use a step stool or lift system for large dogs instead of lifting solo</li>
              <li>🪮 Brush and scissor at angles that allow neutral wrist and elbow posture</li>
              <li>💧 Take micro-breaks to stretch fingers, wrists, and back throughout your shift</li>
            </ul>
            <p><span class="highlight-green">Strong posture = longer, healthier career.</span></p>
          </section>
      
          <section>
            <h4>⚙️ 3. Equipment Handling Safety</h4>
            <ul>
              <li>🔥 Check that clippers, dryers, and shears are properly maintained to prevent overheating or malfunction</li>
              <li>🔌 Never leave plugged-in tools unattended around pets</li>
              <li>🛠️ Immediately report and tag broken or unsafe equipment for repair</li>
              <li>🚿 Use water-safe outlets and ground fault interrupters (GFIs) in bathing areas</li>
            </ul>
          </section>
      
          <section>
            <h4>🧘 4. Mental & Emotional Safety</h4>
            <ul>
              <li>🌬️ Take a breather after handling highly stressful pets or situations</li>
              <li>👥 Lean on your team — ask for backup when needed, never push through alone</li>
              <li>🧠 Use Pawsible’s wellness support resources if feeling burnt out or overwhelmed</li>
            </ul>
            <div class="embedded-visual">
              <img loading="lazy" src="https://pawsible-s3.s3.us-west-1.amazonaws.com/training_images/guide_images/groomerSafetyImage.png" alt="Real-Life Incident Example" class="inline-guide-image" />
            </div>
            <p><span class="highlight-blue">Happy groomers create happy pets — your wellbeing matters here.</span></p>
          </section>
      
          <section>
            <h4>🌱 Final Thought</h4>
            <p>
              <span class="highlight">Professional grooming is a physical art — protect your body, guard your energy, and work smart.</span> 
              We’re committed to a culture of safety, support, and sustainability for every team member.
            </p>
          </section>
        `
      },
      postGroomHandoff: {
        title: "Post-Groom Handoff & Client Notes",
        image: "https://pawsible-s3.s3.us-west-1.amazonaws.com/training_images/guide_images/postGroomHandoffTitle.png",
        body: `
          <section>
            <h3>🎀 Every Detail Matters — Especially After the Groom</h3>
            <p>
              The final impression matters just as much as the first. At Pawsible, we prioritize <span class="highlight">clear communication, client education, and thorough notes</span> to ensure each pet parent leaves informed and confident.
            </p>
          </section>
      
          <section>
            <h4>🤝 1. Handoff Etiquette</h4>
            <ul>
              <li>😊 Greet the client warmly and enthusiastically — this moment is celebratory!</li>
              <li>🎯 Highlight a specific positive about the groom (e.g., “We loved giving Luna her summer trim — she was such a good girl today!”)</li>
              <li>👂 Actively listen if the client has feedback or questions — don't rush the conversation</li>
            </ul>
            <p><span class="highlight-green">The way we close the appointment builds lasting trust — treat it like the most important part of the visit.</span></p>
          </section>
      
          <section>
            <h4>📋 2. Communicating Key Grooming Notes</h4>
            <ul>
              <li>✂️ Explain style notes clearly (e.g., “We went a little shorter on the body per your request, with a rounded teddy bear head.”)</li>
              <li>⚠️ Gently inform about any issues (e.g., matting, sensitive skin, hotspots)</li>
              <li>🌟 Offer grooming maintenance advice if needed (“Brushing once a week will help maintain this cut beautifully.”)</li>
            </ul>
            <p><span class="highlight-blue">Transparency and education empower the pet parent and reinforce your expertise.</span></p>
          </section>
      
          <section>
            <h4>🧾 3. Documentation in Client Profile</h4>
            <ul>
              <li>🗒️ Note haircut lengths used (blade numbers, guard combs, hand scissor areas)</li>
              <li>📋 Record pet behavior: nervousness, tolerance, areas needing breaks</li>
              <li>💬 Add any special client requests for future appointments</li>
              <li>📲 Log notes into the grooming dashboard immediately after each appointment — don’t delay</li>
            </ul>
            <div class="embedded-visual">
              <img loading="lazy" src="https://pawsible-s3.s3.us-west-1.amazonaws.com/training_images/guide_images/postGroomHandoffImage.png" alt="Real-Life Incident Example" class="inline-guide-image" />
            </div>
            <p><span class="highlight-green">Accurate notes make future appointments smoother, faster, and more personalized.</span></p>
          </section>
      
          <section>
            <h4>🔄 4. Handling Rebooking & Upsell Opportunities</h4>
            <ul>
              <li>📅 Politely suggest a rebooking: “Would you like to schedule Luna’s next groom now to keep her coat in top shape?”</li>
              <li>🎁 Mention any promotions, maintenance packages, or add-ons the client may benefit from</li>
            </ul>
            <p><span class="highlight-blue">A warm, professional close turns a one-time visit into a loyal, lifelong client relationship.</span></p>
          </section>
      
          <section>
            <h4>🌱 Final Thought</h4>
            <p>
              <span class="highlight">The final five minutes can determine whether a client returns — or raves about us.</span> 
              Every interaction counts. Let’s make them memorable, personal, and professional.
            </p>
          </section>
        `
      }
}